import store from '@/vuex/store'
import officeConstants from '@/constant/office'

// AHA-43111: on PowerPoint for the web the add-in runs in a cross-origin iframe, where
// window.open (the legacy social-login path) is blocked. The OAuth device grant is the
// only login that can work there, and the social-login UI must stay hidden — both call
// sites need this exact predicate, so it lives here as the single source of truth rather
// than as duplicated inline copies that can drift (see AHA-43111 code review).
//
// Deliberately imports ONLY the store and office constants (no `router`, no `util.js`)
// so this stays a leaf module with no import-cycle risk.
const isPptOnlineAddin = () => {
  return store.state.addinPPTPlatform === officeConstants.OFFICE_ONLINE
    && Boolean(store.getters['featureToggles/canUsePptOnline'])
}

// AHA-43111: "the host is PowerPoint for the web", with no flag in it. The narrower question
// than isPptOnlineAddin() above, and deliberately a separate predicate rather than a parameter:
// that one answers "is the web add-in LIVE", which is what its login/UI callers want, so it
// folds in the flag that unblocks web at all. Callers here are asking about a HOST LIMITATION —
// per-slide binding on the web cannot tell cloned add-in frames apart (see the window.name note
// below and services/office/slide-tags) — and a limitation of the host does not stop being true
// when a rollout flag is off. Anything gated on the flag instead would silently re-open on web
// the moment AHA-43111 were turned off.
//
// Reads reactive store state, not window.Office directly, so Vue computeds built on it
// re-evaluate when captureOfficePlatform() commits the platform during Office boot. Outside the
// add-in `addinPPTPlatform` is empty, so this is false — the right answer for the plain web app.
const isPptOnlineHost = () => {
  return store.state.addinPPTPlatform === officeConstants.OFFICE_ONLINE
}

// AHA-43111: on PowerPoint for the web every cloned add-in frame loads the identical URL and
// shares document.settings live, so the ONE genuinely per-frame identity signal is window.name —
// PowerPoint names each add-in iframe with a string that embeds the id of the slide it lives on,
// e.g. "..._{GUID}4257#1673945135#slide...". The token between the '#'s (1673945135) is the
// slide's persistent id, identical to the suffix of that slide's Rich API id ("257#1673945135").
// Reading it lets a cold-loading frame resolve ITS OWN slide, replacing getSelectedDataAsync,
// which is document-global and hands every simultaneously-booting clone the same (wrong) selected
// slide (the AHA-43111 "wrong slide / slide selector" symptom on multi-insert).
//
// Snapshotted at module evaluation — this leaf module is imported at the top of the office boot
// path, before office.js is injected — in case office.js ever rewrites window.name. In practice it
// leaves the value intact (verified on PowerPoint web), so the live value backs up an empty snapshot.
const PRISTINE_WINDOW_NAME = typeof window !== 'undefined' ? window.name : ''
const SLIDE_TOKEN_PATTERN = /#(\d+)#slide/

// The persistent slide-id token for the slide THIS frame lives on, or null when window.name
// carries no such marker (desktop, or any host that doesn't stamp the frame name). Callers MUST
// handle null — there is no selection-based fallback here by design.
const readOwnSlideTokenFromFrameName = () => {
  const name = PRISTINE_WINDOW_NAME || (typeof window !== 'undefined' ? window.name : '')
  const match = SLIDE_TOKEN_PATTERN.exec(name || '')
  return match ? match[1] : null
}

export default isPptOnlineAddin
export { isPptOnlineAddin, isPptOnlineHost, readOwnSlideTokenFromFrameName }
